import {GaugeValue, MonthlyChartData, WeeklyChartData} from '../types/ManagementOverviewReport.types';
import type {ChartLegendItem} from '../components/ChartLegendPanel';
import {countManagementOverviewChartsPerStation} from './managementOverviewChartIds';

export {countManagementOverviewChartsPerStation};

export const MANAGEMENT_OVERVIEW_COLOR_PRIMARY = '#2D5DE7';
export const MANAGEMENT_OVERVIEW_COLOR_TABLE_HEADER = '#071AC8';
export const MANAGEMENT_OVERVIEW_COLOR_ENERGY_EXPECTED = '#054811';
export const MANAGEMENT_OVERVIEW_COLOR_ENERGY_GENERATED = '#071AC8';
export const MANAGEMENT_OVERVIEW_COLOR_ENERGY_CORRECTED = '#FC531F';
export const MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_POA = '#FC531F';
export const MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_GHI = '#18B483';
export const MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_EXPECTED = '#8CE48F';
export const MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_BAR = '#071AC8';
export const MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_TREND = '#FC531F';
export const MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_BAR = '#73C0DE';
export const MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_TREND = '#18B483';

export const MANAGEMENT_OVERVIEW_GENERATION_LEGEND: ChartLegendItem[] = [
    { label: 'Energia Esperada [MWh]', color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_EXPECTED, variant: 'bar' },
    { label: 'Energia Esperada Corrigida [MWh]', color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_CORRECTED, variant: 'bar' },
    { label: 'Energia Gerada [MWh]', color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_GENERATED, variant: 'bar' },
    { label: 'Irradiação POA [kWh/m2]', color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_POA, variant: 'line' },
    { label: 'Irradiação GHI [kWh/m2]', color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_GHI, variant: 'line' },
];

export const MANAGEMENT_OVERVIEW_IRRADIANCE_LEGEND: ChartLegendItem[] = [
    { label: 'Irradiação POA [kWh/m2]', color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_POA, variant: 'bar' },
    { label: 'Irradiação GHI [kWh/m2]', color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_GHI, variant: 'bar' },
    { label: 'Irradiação Esperada [kwh/m2]', color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_EXPECTED, variant: 'line' },
];

export const MANAGEMENT_OVERVIEW_BASIC_PR_LEGEND: ChartLegendItem[] = [
    { label: 'PR Básico', color: MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_BAR, variant: 'bar' },
    { label: 'Tendência PR Básico', color: MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_TREND, variant: 'line' },
];

export const MANAGEMENT_OVERVIEW_NORMALIZED_PR_LEGEND: ChartLegendItem[] = [
    { label: 'PR Normalizado', color: MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_BAR, variant: 'bar' },
    { label: 'Tendência PR Normalizado', color: MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_TREND, variant: 'line' },
];

export const MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX = 128;
export const MANAGEMENT_OVERVIEW_GAUGE_HEIGHT_PX = 112;

const PRINT_WEEKLY_AXIS_LABEL = {
    fontSize: 8,
    hideOverlap: true,
    rotate: 0,
};

const PRINT_MONTHLY_AXIS_LABEL = {
    fontSize: 7,
    hideOverlap: true,
    rotate: 40,
    margin: 6,
};

const PRINT_CHART_BASE = {
    animation: false,
    animationDuration: 0,
    animationDurationUpdate: 0,
    toolbox: {
        show: false,
    },
    grid: {
        containLabel: true,
        top: 16,
        bottom: 8,
        left: 4,
        right: 28,
    },
};

export function buildManagementOverviewPrintChartBase() {
    return { ...PRINT_CHART_BASE };
}

export function buildWeeklyGenerationChartOption(weekly: WeeklyChartData) {
    return {
        ...buildManagementOverviewPrintChartBase(),
        tooltip: { trigger: 'axis' },
        legend: { show: false },
        xAxis: {
            type: 'category',
            data: weekly.categories,
            axisLabel: PRINT_WEEKLY_AXIS_LABEL,
        },
        yAxis: [
            {
                type: 'value',
                name: 'MWh',
                nameTextStyle: { fontSize: 9 },
                axisLabel: { fontSize: 8 },
            },
            {
                type: 'value',
                name: 'kWh/m²',
                nameTextStyle: { fontSize: 9 },
                axisLabel: { fontSize: 8 },
                splitLine: { show: false },
            },
        ],
        series: [
            {
                name: 'Energia Esperada',
                type: 'bar',
                yAxisIndex: 0,
                barMaxWidth: 16,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_EXPECTED },
                data: weekly.expectedEnergyMwh,
            },
            {
                name: 'Energia Esperada Corrigida',
                type: 'bar',
                yAxisIndex: 0,
                barMaxWidth: 16,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_CORRECTED },
                data: weekly.correctedExpectedEnergyMwh,
            },
            {
                name: 'Energia Gerada',
                type: 'bar',
                yAxisIndex: 0,
                barMaxWidth: 16,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_ENERGY_GENERATED },
                data: weekly.generatedEnergyMwh,
            },
            {
                name: 'Irradiação POA',
                type: 'line',
                yAxisIndex: 1,
                symbol: 'circle',
                symbolSize: 4,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_POA },
                data: weekly.irradiancePoaKwhM2,
            },
            {
                name: 'Irradiação GHI',
                type: 'line',
                yAxisIndex: 1,
                symbol: 'circle',
                symbolSize: 4,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_GHI },
                data: weekly.irradianceGhiKwhM2,
            },
        ],
    };
}

export function buildMonthlyIrradianceChartOption(monthly: MonthlyChartData) {
    return {
        ...buildManagementOverviewPrintChartBase(),
        tooltip: { trigger: 'axis' },
        legend: { show: false },
        xAxis: {
            type: 'category',
            data: monthly.categories,
            axisLabel: PRINT_MONTHLY_AXIS_LABEL,
        },
        yAxis: {
            type: 'value',
            name: 'kWh/m²',
            nameTextStyle: { fontSize: 9 },
            axisLabel: { fontSize: 8 },
        },
        series: [
            {
                name: 'Irradiação POA',
                type: 'bar',
                barMaxWidth: 16,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_POA },
                data: monthly.irradiancePoaKwhM2,
            },
            {
                name: 'Irradiação GHI',
                type: 'bar',
                barMaxWidth: 16,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_GHI },
                data: monthly.irradianceGhiKwhM2,
            },
            {
                name: 'Irradiação Esperada',
                type: 'line',
                symbol: 'circle',
                symbolSize: 4,
                itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_IRRADIANCE_EXPECTED },
                lineStyle: { width: 2 },
                data: monthly.expectedIrradianceKwhM2,
            },
        ],
    };
}

function formatAxisNumber(value: number) {
    if (!Number.isFinite(value)) {
        return '';
    }
    return Number(value).toFixed(0);
}

function prAxisMax(actual: number[], trend: number[]) {
    const values = [...actual, ...trend].filter((value) => Number.isFinite(value));
    const highest = values.length > 0 ? Math.max(...values) : 100;
    return Math.max(100, Math.ceil(highest / 10) * 10);
}

function buildPrBarTrendChartOption(
    monthly: MonthlyChartData,
    actual: number[],
    trend: number[],
    actualName: string,
    trendName: string,
    barColor: string,
    trendColor: string
) {
    return {
        ...buildManagementOverviewPrintChartBase(),
        tooltip: { trigger: 'axis' },
        legend: { show: false },
        xAxis: {
            type: 'category',
            data: monthly.categories,
            axisLabel: PRINT_MONTHLY_AXIS_LABEL,
        },
        yAxis: {
            type: 'value',
            name: '%',
            min: 0,
            max: prAxisMax(actual, trend),
            interval: 20,
            nameTextStyle: { fontSize: 9 },
            axisLabel: {
                fontSize: 8,
                hideOverlap: true,
                formatter: formatAxisNumber,
            },
        },
        series: [
            {
                name: actualName,
                type: 'bar',
                barMaxWidth: 22,
                itemStyle: { color: barColor },
                data: actual,
            },
            {
                name: trendName,
                type: 'line',
                symbol: 'circle',
                symbolSize: 4,
                itemStyle: { color: trendColor },
                lineStyle: { width: 2 },
                data: trend,
            },
        ],
    };
}

export function buildBasicPrChartOption(monthly: MonthlyChartData) {
    return buildPrBarTrendChartOption(
        monthly,
        monthly.basicPrPercent,
        monthly.basicPrTrendPercent,
        'PR Básico',
        'Tendência PR Básico',
        MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_BAR,
        MANAGEMENT_OVERVIEW_COLOR_PR_BASIC_TREND
    );
}

export function buildNormalizedPrChartOption(monthly: MonthlyChartData) {
    return buildPrBarTrendChartOption(
        monthly,
        monthly.normalizedPrPercent,
        monthly.normalizedPrTrendPercent,
        'PR Normalizado',
        'Tendência PR Normalizado',
        MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_BAR,
        MANAGEMENT_OVERVIEW_COLOR_PR_NORMALIZED_TREND
    );
}

export function buildGaugeChartOption(label: string, gauge: GaugeValue) {
    const maxValue = Math.max(gauge.expectedPercent, gauge.actualPercent, 100);

    return {
        animation: false,
        animationDuration: 0,
        toolbox: { show: false },
        series: [
            {
                name: `${label} esperado`,
                type: 'gauge',
                min: 0,
                max: maxValue,
                startAngle: 90,
                endAngle: -270,
                pointer: { show: false },
                progress: {
                    show: true,
                    overlap: false,
                    roundCap: true,
                    clip: true,
                    itemStyle: { color: '#C5D4F5' },
                },
                axisLine: {
                    lineStyle: { width: 14, color: [[1, '#E8ECF8']] },
                },
                splitLine: { show: false },
                axisTick: { show: false },
                axisLabel: { show: false },
                center: ['50%', '50%'],
                radius: '78%',
                detail: { show: false },
                title: { show: false },
                data: [{ value: gauge.expectedPercent, name: 'Esperado' }],
            },
            {
                name: label,
                type: 'gauge',
                min: 0,
                max: maxValue,
                startAngle: 90,
                endAngle: -270,
                pointer: { show: false },
                progress: {
                    show: true,
                    overlap: false,
                    roundCap: true,
                    clip: true,
                    itemStyle: { color: MANAGEMENT_OVERVIEW_COLOR_PRIMARY },
                },
                axisLine: {
                    lineStyle: { width: 14, color: [[1, 'transparent']] },
                },
                splitLine: { show: false },
                axisTick: { show: false },
                axisLabel: { show: false },
                center: ['50%', '50%'],
                radius: '70%',
                detail: {
                    formatter: `${gauge.actualPercent.toFixed(1)}%`,
                    fontSize: 11,
                    color: '#051f36',
                    offsetCenter: [0, 0],
                },
                title: { show: false },
                data: [{ value: gauge.actualPercent, name: label }],
            },
        ],
    };
}

/** @deprecated use buildMonthlyIrradianceChartOption */
export function buildWeeklyIrradianceChartOption(weekly: WeeklyChartData) {
    return buildMonthlyIrradianceChartOption({
        categories: weekly.categories,
        irradiancePoaKwhM2: weekly.irradiancePoaKwhM2,
        irradianceGhiKwhM2: weekly.irradianceGhiKwhM2,
        expectedIrradianceKwhM2: weekly.irradiancePoaKwhM2,
        basicPrPercent: [],
        basicPrTrendPercent: [],
        normalizedPrPercent: [],
        normalizedPrTrendPercent: [],
    });
}
